/**
 * How the validation kiosk (screens/Validate.tsx) reads a refusal.
 *
 * The server tells the tablet "no" in two different ways that need two
 * different screens, and a clerk should never have to decode either one:
 *
 *   - EXHAUSTED: the allowance is spent. Working as configured.
 *   - UNAVAILABLE: validation cannot be applied right now (program off or
 *     expired, or the server could not read its own count). Not a statement
 *     about the allowance at all.
 *
 * In both, the driver is not stuck: they can still park and pay for
 * themselves. That is the line the generic server error never said.
 *
 * UI-ONLY. The refusals are decided in `api/src/validations/` (caps.ts,
 * evalidate.ts); this file only recognizes what they already send. The server
 * has no machine-readable refusal code on the kiosk routes today (a refusal is
 * `status: 'rejected' | 'inactive'` plus a message), so the shared copy is the
 * code, and `kioskRefusal.test.ts` reads caps.ts to fail if either string
 * drifts on one side only.
 *
 * ⚠️ UNKNOWN IS NOT ZERO. A count the server could not read arrives as an
 * absent `remaining` (GET /validations omits it) or as the "can't confirm"
 * refusal. Neither is exhaustion. Rendering them as "No validations remaining"
 * would tell a clerk to turn away a customer who is entitled to the credit,
 * which is the mistake caps.ts exists to prevent on the server side.
 */
import type { Validation } from './api'
import { isCommercialBoundReached } from './offerEligibility'

export type KioskRefusal = 'exhausted' | 'unavailable'

/** caps.ts VALIDATION_EXHAUSTED_MESSAGE: inventory, spend limit and prepaid block all share it. */
export const EXHAUSTED_MESSAGE = 'This validation is out of uses for now.'
/** Start of caps.ts CAP_UNVERIFIED_MESSAGE: a cap the server could not read. */
export const UNVERIFIED_MESSAGE_START = "We can't confirm this validation's remaining uses right now"
/** Start of evalidate.ts' 503 body when the ledger cannot answer. */
export const LEDGER_UNAVAILABLE_START = 'Validation ledger unavailable'

const norm = (s: string | undefined): string => (s ?? '').trim()

/** The refusal in a 200 status envelope from evalidate / free-session / invite, or null. */
export function refusalOfResult(r: { status?: string; message?: string }): KioskRefusal | null {
  if (r.status === 'inactive') return 'unavailable'
  if (r.status !== 'rejected') return null
  return refusalOfMessage(r.message)
}

/** The refusal in a thrown API error (non-2xx), or null for an ordinary failure. */
export function refusalOfError(e: unknown): KioskRefusal | null {
  if (!(e instanceof Error)) return null
  const status = (e as Error & { status?: number }).status
  const byMessage = refusalOfMessage(e.message) ?? (norm(e.message).startsWith(LEDGER_UNAVAILABLE_START) ? 'unavailable' : null)
  if (byMessage) return byMessage
  return status === 502 || status === 503 || status === 504 ? 'unavailable' : null
}

function refusalOfMessage(message: string | undefined): KioskRefusal | null {
  const m = norm(message)
  if (m === EXHAUSTED_MESSAGE) return 'exhausted'
  if (m.startsWith(UNVERIFIED_MESSAGE_START)) return 'unavailable'
  return null
}

export type AllowanceState = 'unmetered' | 'available' | 'exhausted' | 'unknown'

/**
 * What the offer list says about the allowance, BEFORE anyone types a lookup.
 * Only a real number at or below zero is exhausted. A capped offer with no
 * `remaining` (the server could not count) is `unknown` and renders as nothing.
 *
 * A REPORTED commercial wall (the spend limit reached, the prepaid block used
 * up: lib/offerEligibility.ts) is exhausted too, and renders the same panel:
 * the server refuses both with the one shared copy, so the screen does too.
 * An absent or unknown commercial answer changes nothing here.
 */
export function allowanceState(offer: Pick<Validation, 'inventory' | 'remaining' | 'commercial'> | null): AllowanceState {
  if (offer && isCommercialBoundReached(offer)) return 'exhausted'
  if (!offer || !offer.inventory) return 'unmetered'
  const left = offer.remaining
  if (typeof left !== 'number' || !Number.isFinite(left)) return 'unknown'
  return left <= 0 ? 'exhausted' : 'available'
}
